// Core
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  Button,
  Box,
  Grid,
  Flex,
  Text,
  FormControl,
  Input,
  useColorModeValue,
  useDisclosure,
  useBreakpointValue
} from '@chakra-ui/react';

// Accets
import { apiService } from 'services/ApiService';
import { ReactComponent as EditIcon } from 'assets/svg/edit.svg';
import { ReactComponent as CancelIcon } from 'assets/svg/cancel.svg';

// Components
import Card from 'components/Card/Card';
import ErrorCard from 'components/Form/Errors';
import EditPhone from 'components/Form/EditPhone';
import EditSelect from 'components/Form/EditSelect';
import CodeModal from 'components/Modals/CodeModal';

const ChangePhone = ({ userData }) => {
	const bg = useColorModeValue('white', '#0A1027');
    const boxShadow = useColorModeValue('unset', '0px 1px 3px 0px rgba(0, 0, 0, 0.10),0px 1px 2px 0px rgba(0, 0, 0, 0.06)');

	const [object, setObject] = useState({ phone: userData.phone });
	const [objectErrors, setObjectErrors] = useState([]);
	const [errorCard, setErrorCard] = useState();

	const setData = useCallback((maxValue, target, key) => {
		if (target.length < maxValue) {
		  setObjectErrors([...objectErrors, key]);
		} else {
		  setObjectErrors(objectErrors.filter(error => error !== key));
		}
		setObject({...object, [key]: target}); 
	}, [object, objectErrors, setObject, setObjectErrors]);

	const handleField = useMemo(() => {
		const fieldConfigs = [
		  { field: 'phone', maxValue: 4 }
		];

		setErrorCard();

		return fieldConfigs.reduce((acc, { field, maxValue }) => {
		  acc[field] = (e) => setData(maxValue, e.target.value, field);
		  return acc;
		}, {});
	}, [setData]);

	const handleChange = async (code) => {
		setErrorCard(false);

		if (objectErrors.length > 0) {
	      setErrorCard(true);
	      return;
	    }

	    if(object.phone === userData.phone){
	      setErrorCard('You can`t change to the same phone address');
	      setTimeout(function() { onClose(); }, 1000);
	      return;	
	    }

	    let data = object;
	    if(code){
	    	data = { ...data, code};
	    }

		try {
			const response = await apiService.updateUserPhone(data);
			if(response.data === false || 
				response.data.status === false || 
				response.statusCode !== 200 || 
				response.statusCode === 404){
				//setErrorCard(response.data.errorMessage);
				setIsValidCode(false);
				return;
			} else if(response.statusCode === 200 && response.data.status === true){
	          startTimer(); // stop
	          startTimer(); // start
	        }
		} catch(e){
			setErrorCard('This phone already exists or an unknown error occurred, try changing the fields or refreshing the page');
			setTimeout(function() { onClose(); }, 1000);
			return;
		}

		if(code){
			setEditMode(false);	
			onClose();		
		}
	};

	const [editMode, setEditMode] = useState(false);

	const { isOpen, onClose, onOpen } = useDisclosure();
	const [seconds, setSeconds] = useState(59);
  	const [timerActive, setTimerActive] = useState(false);
	useEffect(() => {
	    let interval = null;
	    
	    if (timerActive && seconds > 0) {
	      interval = setInterval(() => {
	        setSeconds(seconds => seconds - 1);
	      }, 1000);
	    } else if (!timerActive && seconds !== 0) {
	      clearInterval(interval);
	    } else if (seconds === 0) {
	      setTimerActive(false);
	    }

	    return () => clearInterval(interval);
	}, [timerActive, seconds]);
	const [isValidCode, setIsValidCode] = useState(true);
	const startTimer = () => {
	    setTimerActive(true);
	    setSeconds(59);  
	};

	const isMobile = useBreakpointValue({ base: true, md: false });
 
	return (
		<Box mt={{base: '-4px', md: '8px'}}>
			{ errorCard && ( <ErrorCard error={errorCard} /> )}

			<Card mt={{base: '4px'}} padding={{base: '16px 12px 0px 12px', md: '16px 16px 24px 16px'}} bg={bg} boxShadow={boxShadow} overflow="hidden">
				<Flex alignItems="center" p="0 0 0 18px" mb="24px" justifyContent="space-between">
					<Text variant="title-lg" fontSize="18px" fontStyle="normal" fontWeight="800" letterSpacing="-1px" textTransform="uppercase">
						Contact
					</Text>

					<>
						{ editMode ? (
							<Flex gap="12px">
								<Button onClick={() => {setEditMode(!editMode); setErrorCard();}} variant="delete-md" w={{base: '44px', md: '88px'}}>{isMobile ? (<CancelIcon />) : 'Cancel'}</Button>
								<Button onClick={() => {handleChange(); onOpen(); startTimer();}}  variant="button-md" w={{base: '72px', md: '124px'}}>Save</Button>
							</Flex>
						) : (
							<Button onClick={() => setEditMode(!editMode)} variant="solid-md" w="88px">Edit <EditIcon fontSize="24px" style={{marginLeft: '6px'}} /></Button>
						)}
					</>
				</Flex>

				<Box mt="2px" zIndex="2">
			        <EditPhone editMode={editMode} id="phone" label="Phone Number" type="text" 
			        	value={object.phone} isValid={true} 
			        	onChange={(value) => handleField.phone({ target: { value } })} />
				</Box>
			</Card>

			<CodeModal
		        handle={handleChange}
		        seconds={seconds} 
			    startTimer={startTimer} 
			    timerActive={timerActive} 
			    isValidCode={isValidCode} 
			    setIsValidCode={setIsValidCode}
		        isOpen={isOpen}
		        onClose={onClose}
		        errorCard={errorCard}
		        title="Change phone"
		        description="A verification code has been sent to the new phone you provided. Please enter it below." 
		        />
		</Box>
	);
};


export default ChangePhone;